import { s } from '@/theme/tokens';
import { ChartEmptyState, CHART_EMPTY_STATE_NO_DATA_LABEL } from '@/ui/composites';
import { StatusPieCardSkeleton } from '@/ui/composites/status/StatusPieCardSkeleton';
import { Box, Skeleton } from '@/ui/primitives';
import { buildMiniMetricChartEmptyStateRecipe } from './chartPanelSurfaceSx';
export {
  resolveStackedStatusCardIcon,
  resolveStackedStatusCardIconColor,
} from './StackedMetricsStatusChartSection.icons';
import type { ReactElement } from 'react';

const LONG_SKELETON_RADIUS = '999px';
const chartSkeletonBarHeight = 14;
const shortMonthDayFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
});

const formatShortMonthDay = (label: string): string => {
  const trimmed = label.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return shortMonthDayFormatter.format(new Date(Number(year), Number(month) - 1, Number(day)));
  }

  const slashMatchWithYear = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slashMatchWithYear) {
    const [, day, month, year] = slashMatchWithYear;
    return shortMonthDayFormatter.format(new Date(Number(year), Number(month) - 1, Number(day)));
  }

  const slashMatch = /^(\d{1,2})\/(\d{1,2})$/.exec(trimmed);
  if (slashMatch) {
    const [, day, month] = slashMatch;
    const currentYear = new Date().getFullYear();
    return shortMonthDayFormatter.format(new Date(currentYear, Number(month) - 1, Number(day)));
  }

  return trimmed;
};

export const renderMiniMetricEmptyState = (_title: string, height: number): ReactElement => {
  const emptyStateRecipe = buildMiniMetricChartEmptyStateRecipe({ height });

  return (
    <Box sx={emptyStateRecipe.containerSx}>
      <ChartEmptyState
        title={CHART_EMPTY_STATE_NO_DATA_LABEL}
        contentSx={emptyStateRecipe.contentSx}
        variant="metricCard"
      />
    </Box>
  );
};

export const renderMiniMetricLoadingState = (height: number): ReactElement => (
  <Box aria-hidden="true" sx={{ height, width: '100%' }} />
);

export const renderStatusPieLoadingState = (height: number): ReactElement => {
  const legendRows = [{ width: '100%' }, { width: '68%' }] as const;
  const pieSize = Math.min(192, Math.max(152, Math.round(height * 0.58)));
  const pieInnerSize = Math.round(pieSize * 0.56);
  return (
    <Box
      sx={{
        width: '100%',
        height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'stretch',
        gap: s('md'),
        pt: s('none'),
        transform: `translateY(${s('2xl')})`,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          flex: '1 1 auto',
          minHeight: 0,
          placeItems: 'center',
          width: '100%',
        }}
      >
        <StatusPieCardSkeleton size={pieSize} innerSize={pieInnerSize} />
      </Box>
      <Box
        sx={{
          flex: '0 0 auto',
          width: 'min(100%, 148px)',
          display: 'grid',
          rowGap: s('md'),
          pt: s('none'),
          justifyItems: 'stretch',
          mx: 'auto',
          mb: s('none'),
        }}
      >
        {legendRows.map((rowConfig, index) => (
          <Skeleton
            key={`pie-legend-skeleton-${index}`}
            variant="rounded"
            width={rowConfig.width}
            height={chartSkeletonBarHeight}
            sx={{
              display: 'block',
              minHeight: chartSkeletonBarHeight,
              maxHeight: chartSkeletonBarHeight,
              borderRadius: LONG_SKELETON_RADIUS,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export const renderStatusChartLegendLoadingState = (): ReactElement => {
  const itemWidths = [58, 52, 48] as const;
  return (
    <Box
      sx={{
        width: 'auto',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        alignItems: 'center',
        columnGap: s('lg'),
        rowGap: s('md'),
        minWidth: 0,
        boxSizing: 'border-box',
        transform: `translateY(${s('xs')})`,
      }}
    >
      {itemWidths.map((width, index) => (
        <Box
          key={`status-legend-skeleton-${index}`}
          sx={{
            alignItems: 'center',
            display: 'inline-flex',
            flexShrink: 0,
            gap: s('md'),
          }}
        >
          <Skeleton
            variant="rounded"
            width={width}
            height={chartSkeletonBarHeight}
            sx={{
              display: 'block',
              minHeight: chartSkeletonBarHeight,
              maxHeight: chartSkeletonBarHeight,
              borderRadius: LONG_SKELETON_RADIUS,
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

export const renderMiniMetricHeaderLoadingState = (): ReactElement => (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: s('md'),
    }}
  >
    <Skeleton
      variant="rounded"
      width={92}
      height={chartSkeletonBarHeight}
      sx={{ borderRadius: LONG_SKELETON_RADIUS }}
    />
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <Skeleton
        variant="rounded"
        width={78}
        height={chartSkeletonBarHeight}
        sx={{ borderRadius: LONG_SKELETON_RADIUS }}
      />
    </Box>
  </Box>
);

export const formatStackedStatusChartLabel = (label: string): string => {
  return formatShortMonthDay(label);
};
